import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Common
    title: "Teacher Results",
    language: "Language",
    back: "Back",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    print: "Print",
    loading: "Loading...",
    yes: "Yes",
    no: "No",
    completed: "Completed",
    incomplete: "Incomplete",
    clearFilters: "Clear Filters",

    // Authentication
    adminLogin: "Admin Login",
    loginToContinue: "Please login to continue",
    username: "Username",
    password: "Password",
    enterUsername: "Enter your username",
    enterPassword: "Enter your password",
    login: "Login",
    logout: "Logout",
    invalidCredentials: "Invalid username or password",

    // Lookup Page
    registrationNumber: "Registration Number",
    displayResults: "Display Results",
    enterRegNumber: "Enter your registration number",

    // Results Page
    totalScore: "Total Score (out of 100)",
    categoryScores: "Category Scores (each out of 5, 20% each)",
    originality: "Originality & Uniqueness",
    relevance: "Relevance",
    skills: "Skills",
    presentation: "Presentation",
    educationalValue: "Educational Value",
    backToLookup: "Back to Lookup",
    saveAsPDF: "Save as PDF",

    // Status
    topDistinguished: "Top Distinguished Project",
    passed: "Passed",
    didNotPass: "Did not pass",
    assignmentsComplete: "All assignments finished",
    assignmentsIncomplete: "Assignments incomplete",
    assignments: "Assignments",
    assignmentsWarning: "Warning: Marking assignments as incomplete will automatically set the status to 'Did not pass', regardless of score.",
    allAssignments: "All Assignment Statuses",

    // Status Messages
    statusMessages: {
      TOP_DISTINGUISHED: "Congratulations! Your coursework and student projects have passed, and your project is among the most distinguished. Please use the link below to register your team for the final ceremony pitching.",
      PASSED: "Congratulations! Your coursework and student projects have passed, and you've completed all project requirements. Please use the link below to register yourself to attend the final ceremony pitching.",
      FAIL: "Thank you for being part of the project and for all your efforts. Unfortunately, you did not meet the exam and assignment requirements. However, we're happy to invite you to attend the ceremony. Please register using the link below."
    },

    // Status Notes
    statusNotes: {
      TOP_DISTINGUISHED: "Congratulations! You reached the Top Distinguished Project.",
      PASSED: "You passed.",
      FAIL: "You did not pass.",
      ASSIGNMENT_COMPLETE: "All required assignments have been completed."
    },

    // Admin
    adminDashboard: "Admin Dashboard",
    teacherRecords: "Teacher Records",
    createNew: "Create New",
    teacherDetails: "Teacher Details",
    identity: "Identity",
    scores: "Scores",
    computed: "Computed Values",
    fullName: "Full Name",
    school: "School",
    city: "City",
    governorate: "Governorate",
    email: "Email",
    phone: "Phone",
    totalRaw: "Total Raw",
    totalPercentage: "Total Percentage",
    statusCode: "Status Code",
    lastUpdated: "Last Updated",
    selectGovernorate: "Select Governorate",

    // Governorates
    governorates: {
      amman: "Amman",
      irbid: "Irbid",
      balqa: "Balqa",
      zarqa: "Zarqa",
      mafraq: "Mafraq",
      jerash: "Jerash",
      ajloun: "Ajloun",
      madaba: "Madaba",
      karak: "Karak",
      tafilah: "Tafilah",
      maan: "Ma'an",
      aqaba: "Aqaba"
    },

    // Message Management
    messageManagement: "Message Management",
    messageSettings: "Message Settings",
    topDistinguishedMessage: "Top Distinguished Message (85%+)",
    passedMessage: "Passed Message (50%-85%)",
    failedMessage: "Failed Message (<50%)",
    messageUpdated: "Messages updated successfully",

    // Link Management
    linkManagement: "Registration Links",
    linkSettings: "Registration Link Settings",
    topDistinguishedLink: "Top Distinguished Registration Link",
    passedLink: "Passed Registration Link",
    failedLink: "Failed Registration Link",
    linkUpdated: "Links updated successfully",
    registerTeamText: "Register Your Team",
    registerAttendText: "Register for the Ceremony",

    // Filters
    allStatuses: "All Statuses",
    minScore: "Min Score",
    maxScore: "Max Score",

    // Actions
    actions: "Actions",
    confirmDelete: "Are you sure you want to delete this teacher record?",
    recalculateAll: "Recalculate All",
    exportCSV: "Export CSV",
    exportPDF: "Export PDF",
    importCSV: "Import CSV",

    // Error Messages
    noResults: "No results found. Please check your registration number.",
    invalidScore: "Each category must be an integer between 0 and 5.",
    duplicateRegistration: "This registration number already exists.",
    requiredField: "This field is required.",
    invalidEmail: "Please enter a valid email address.",
    invalidUrl: "Please enter a valid URL.",

    // Success Messages
    recordSaved: "Teacher record saved successfully.",
    recordDeleted: "Teacher record deleted successfully.",
    dataImported: "Data imported successfully.",
    dataExported: "Data exported successfully."
  },
  ar: {
    // Common
    title: "نتائج المعلمين",
    language: "اللغة",
    back: "رجوع",
    save: "حفظ",
    cancel: "إلغاء",
    delete: "حذف",
    edit: "تعديل",
    create: "إنشاء",
    search: "بحث",
    filter: "تصفية",
    export: "تصدير",
    import: "استيراد",
    print: "طباعة",
    loading: "جاري التحميل...",
    yes: "نعم",
    no: "لا",
    completed: "مكتمل",
    incomplete: "غير مكتمل",
    clearFilters: "مسح التصفية",

    // Authentication
    adminLogin: "تسجيل دخول المشرف",
    loginToContinue: "يرجى تسجيل الدخول للمتابعة",
    username: "اسم المستخدم",
    password: "كلمة المرور",
    enterUsername: "أدخل اسم المستخدم",
    enterPassword: "أدخل كلمة المرور",
    login: "تسجيل الدخول",
    logout: "تسجيل الخروج",
    invalidCredentials: "اسم المستخدم أو كلمة المرور غير صحيحة",

    // Lookup Page
    registrationNumber: "رقم التسجيل",
    displayResults: "عرض النتائج",
    enterRegNumber: "أدخل رقم التسجيل الخاص بك",

    // Results Page
    totalScore: "المجموع من 100",
    categoryScores: "علامات الفئات (لكل فئة من 5 وبوزن 20%)",
    originality: "الأصالة والتميّز",
    relevance: "الملاءمة",
    skills: "المهارات",
    presentation: "العرض",
    educationalValue: "القيمة التعليمية",
    backToLookup: "العودة للبحث",
    saveAsPDF: "حفظ كملف PDF",

    // Status
    topDistinguished: "المشروع المتميز",
    passed: "ناجح",
    didNotPass: "راسب",
    assignmentsComplete: "الواجبات مكتملة",
    assignmentsIncomplete: "الواجبات غير مكتملة",
    assignments: "الواجبات",
    assignmentsWarning: "تحذير: وضع علامة على الواجبات كغير مكتملة سيؤدي تلقائيًا إلى تعيين الحالة إلى 'راسب'، بغض النظر عن الدرجة.",
    allAssignments: "جميع حالات الواجبات",

    // Status Messages
    statusMessages: {
      TOP_DISTINGUISHED: "تهانينا! تم اجتياز أعمالك ومشاريع الطلبة بنجاح، ومشروعك من بين المشاريع المتميزة. يرجى استخدام الرابط أدناه لتسجيل فريقك للمشاركة في عرض الحفل النهائي.",
      PASSED: "تهانينا! تم اجتياز أعمالك ومشاريع الطلبة بنجاح، وقد أكملت جميع متطلبات المشروع. يرجى استخدام الرابط أدناه لتسجيل نفسك لحضور عرض الحفل النهائي.",
      FAIL: "شكرًا لكونك جزءًا من المشروع ولجميع جهودك. للأسف، لم تستوفِ متطلبات الاختبارات والواجبات. ومع ذلك، يسعدنا دعوتك لحضور الحفل. يرجى التسجيل عبر الرابط أدناه."
    },

    // Status Notes
    statusNotes: {
      TOP_DISTINGUISHED: "تهانينا! لقد وصلت إلى مرتبة المشروع المتميز.",
      PASSED: "لقد نجحت.",
      FAIL: "لم تنجح.",
      ASSIGNMENT_COMPLETE: "تم إكمال جميع الواجبات المطلوبة."
    },

    // Admin
    adminDashboard: "لوحة تحكم الإدارة",
    teacherRecords: "سجلات المعلمين",
    createNew: "إنشاء جديد",
    teacherDetails: "تفاصيل المعلم",
    identity: "الهوية",
    scores: "الدرجات",
    computed: "القيم المحسوبة",
    fullName: "الاسم الكامل",
    school: "المدرسة",
    city: "المدينة",
    governorate: "المحافظة",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    totalRaw: "المجموع الخام",
    totalPercentage: "النسبة المئوية",
    statusCode: "رمز الحالة",
    lastUpdated: "آخر تحديث",
    selectGovernorate: "اختر المحافظة",

    // Governorates
    governorates: {
      amman: "عمّان",
      irbid: "إربد",
      balqa: "البلقاء",
      zarqa: "الزرقاء",
      mafraq: "المفرق",
      jerash: "جرش",
      ajloun: "عجلون",
      madaba: "مأدبا",
      karak: "الكرك",
      tafilah: "الطفيلة",
      maan: "معان",
      aqaba: "العقبة"
    },

    // Message Management
    messageManagement: "إدارة الرسائل",
    messageSettings: "إعدادات الرسائل",
    topDistinguishedMessage: "رسالة المشروع المتميز (85%+)",
    passedMessage: "رسالة النجاح (50%-85%)",
    failedMessage: "رسالة الرسوب (<50%)",
    messageUpdated: "تم تحديث الرسائل بنجاح",

    // Link Management
    linkManagement: "روابط التسجيل",
    linkSettings: "إعدادات روابط التسجيل",
    topDistinguishedLink: "رابط تسجيل المشروع المتميز",
    passedLink: "رابط تسجيل الناجحين",
    failedLink: "رابط تسجيل الراسبين",
    linkUpdated: "تم تحديث الروابط بنجاح",
    registerTeamText: "سجل فريقك",
    registerAttendText: "سجل للحضور",

    // Filters
    allStatuses: "جميع الحالات",
    minScore: "أقل درجة",
    maxScore: "أعلى درجة",

    // Actions
    actions: "الإجراءات",
    confirmDelete: "هل أنت متأكد من حذف سجل هذا المعلم؟",
    recalculateAll: "إعادة حساب الكل",
    exportCSV: "تصدير CSV",
    exportPDF: "تصدير PDF",
    importCSV: "استيراد CSV",

    // Error Messages
    noResults: "لا توجد نتائج. يرجى التحقق من رقم التسجيل.",
    invalidScore: "يجب أن تكون علامة كل فئة عددًا صحيحًا بين 0 و5.",
    duplicateRegistration: "رقم التسجيل موجود بالفعل.",
    requiredField: "هذا الحقل مطلوب.",
    invalidEmail: "يرجى إدخال عنوان بريد إلكتروني صالح.",
    invalidUrl: "يرجى إدخال رابط صالح.",

    // Success Messages
    recordSaved: "تم حفظ سجل المعلم بنجاح.",
    recordDeleted: "تم حذف سجل المعلم بنجاح.",
    dataImported: "تم استيراد البيانات بنجاح.",
    dataExported: "تم تصدير البيانات بنجاح."
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);
  const [customMessages, setCustomMessages] = useState({
    TOP_DISTINGUISHED: null,
    PASSED: null,
    FAIL: null
  });
  const [registrationLinks, setRegistrationLinks] = useState({
    TOP_DISTINGUISHED: "https://fill.formnx.com/f/finalist-85sj20",
    PASSED: "https://fill.formnx.com/f/15rv10",
    FAIL: "https://fill.formnx.com/f/15rv10"
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
    setIsRTL(savedLanguage === 'ar');
    document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLanguage;

    // Load custom messages if available
    const savedMessages = localStorage.getItem('customMessages');
    if (savedMessages) {
      try {
        setCustomMessages(JSON.parse(savedMessages));
      } catch (err) {
        console.error("Error parsing saved messages:", err);
        // Reset to default if there's an error
        localStorage.removeItem('customMessages');
      }
    }

    // Load custom registration links if available
    const savedLinks = localStorage.getItem('registrationLinks');
    if (savedLinks) {
      try {
        setRegistrationLinks(JSON.parse(savedLinks));
      } catch (err) {
        console.error("Error parsing saved links:", err);
        localStorage.removeItem('registrationLinks');
      }
    }
  }, []);

  const switchLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    setIsRTL(newLanguage === 'ar');
    localStorage.setItem('language', newLanguage);
    document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage;
  };

  const updateCustomMessages = (newMessages) => {
    setCustomMessages(newMessages);
    localStorage.setItem('customMessages', JSON.stringify(newMessages));
  };

  const updateRegistrationLinks = (newLinks) => {
    setRegistrationLinks(newLinks);
    localStorage.setItem('registrationLinks', JSON.stringify(newLinks));
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      if (!value?.[k]) {
        break;
      }
      value = value[k];
    }

    // If this is a status message and we have a custom one, use it
    if (keys.length === 2 && keys[0] === 'statusMessages' && customMessages[keys[1]]) {
      return customMessages[keys[1]];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        isRTL,
        switchLanguage,
        t,
        customMessages,
        updateCustomMessages,
        registrationLinks,
        updateRegistrationLinks
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};